import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Search, Plus, Dumbbell, ChevronRight, Trash2, Pencil, Heart, Layers, Share2, Copy, Loader2, Flame, Download, Star, History, Send, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import SupersetEditor from "../components/SupersetEditor";
import CustomExerciseDialog from "../components/CustomExerciseDialog";
import RequestExerciseDialog from "../components/RequestExerciseDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

export default function Library() {
  const [tab, setTab] = useState("workouts");
  const [exercises, setExercises] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [supersets, setSupersets] = useState([]);
  const [editingSS, setEditingSS] = useState(null);
  const [showSSEditor, setShowSSEditor] = useState(false);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [showCustomEx, setShowCustomEx] = useState(false);
  const [showRequestEx, setShowRequestEx] = useState(false);
  const nav = useNavigate();

  useEffect(() => { loadAll(); }, []);
  const loadAll = async () => {
    const [e, c, w, s] = await Promise.all([
      api.get("/exercises"),
      api.get("/exercises/categories"),
      api.get("/workouts"),
      api.get("/supersets"),
    ]);
    setExercises(e.data);
    setCategories(["All", ...c.data]);
    setWorkouts(w.data);
    setSupersets(s.data);
  };

  const filtered = useMemo(() => {
    return exercises.filter((x) =>
      (category === "All" || x.category === category) &&
      (!query || x.name.toLowerCase().includes(query.toLowerCase()))
    );
  }, [exercises, category, query]);

  const deleteWorkout = async (id) => {
    if (!window.confirm("Delete this workout?")) return;
    await api.delete(`/workouts/${id}`);
    setWorkouts((w) => w.filter((x) => x.workout_id !== id));
    toast.success("Workout deleted");
  };

  const deleteSuperset = async (id) => {
    if (!window.confirm("Delete this superset?")) return;
    await api.delete(`/supersets/${id}`);
    setSupersets((p) => p.filter((x) => x.superset_id !== id));
    toast.success("Superset deleted");
  };
  const reloadExercises = async () => {
    const [e, c] = await Promise.all([api.get("/exercises"), api.get("/exercises/categories")]);
    setExercises(e.data);
    setCategories(["All", ...c.data]);
  };
  const deleteCustomExercise = async (ex) => {
    if (!window.confirm(`Delete "${ex.name}"? Workouts using it keep their logged history.`)) return;
    try {
      await api.delete(`/exercises/custom/${ex.exercise_id}`);
      setExercises((p) => p.filter((x) => x.exercise_id !== ex.exercise_id));
      toast.success("Custom exercise deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  const submitCustomToLibrary = async (ex) => {
    try {
      await api.post("/exercise-requests", { name: ex.name, notes: "Submitted from my custom exercises", custom_exercise_id: ex.exercise_id });
      toast.success(`"${ex.name}" sent to the coach for the public library`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
  };
  const editSuperset = (ss) => { setEditingSS(ss); setShowSSEditor(true); };
  const newSuperset = () => { setEditingSS(null); setShowSSEditor(true); };
  const handleSSSaved = (saved) => {
    setSupersets((p) => {
      const exists = p.find((x) => x.superset_id === saved.superset_id);
      return exists
        ? p.map((x) => x.superset_id === saved.superset_id ? saved : x)
        : [saved, ...p];
    });
  };
  const [shareSS, setShareSS] = useState(null);  // {superset, url} when modal open
  const [sharing, setSharing] = useState(null);
  const openShare = async (ss) => {
    setSharing(ss.superset_id);
    try {
      const { data } = await api.post(`/supersets/${ss.superset_id}/share`);
      const fullUrl = `${window.location.origin}${data.url}`;
      setShareSS({ superset: ss, url: fullUrl, token: data.share_token });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate link");
    } finally {
      setSharing(null);
    }
  };

  // Workout share — same flow as supersets but for workouts.
  const [shareWk, setShareWk] = useState(null);
  const [sharingWk, setSharingWk] = useState(null);
  const openShareWorkout = async (wk) => {
    setSharingWk(wk.workout_id);
    try {
      const { data } = await api.post(`/workouts/${wk.workout_id}/share`);
      const fullUrl = `${window.location.origin}${data.url}`;
      setShareWk({ workout: wk, url: fullUrl, token: data.share_token });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate link");
    } finally {
      setSharingWk(null);
    }
  };
  const copyShareWk = async () => {
    if (!shareWk) return;
    try {
      await navigator.clipboard.writeText(shareWk.url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };
  const nativeShareWk = async () => {
    if (!shareWk) return;
    const text = `Try my "${shareWk.workout.name}" workout on GYM with GRIZZ 💪`;
    if (navigator.share) {
      try { await navigator.share({ title: "GYM with GRIZZ Workout", text, url: shareWk.url }); } catch {}
    } else {
      copyShareWk();
    }
  };
  const copyShare = async () => {
    if (!shareSS) return;
    try {
      await navigator.clipboard.writeText(shareSS.url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };
  const nativeShare = async () => {
    if (!shareSS) return;
    const text = `Try my "${shareSS.superset.name}" superset on GYM with GRIZZ 💪`;
    if (navigator.share) {
      try { await navigator.share({ title: "GYM with GRIZZ Superset", text, url: shareSS.url }); } catch {}
    } else {
      copyShare();
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mb-1">Library</h1>
      <div className="text-xs tracking-[0.2em] uppercase text-neutral-400 mb-4">Exercises & Saved Workouts</div>

      <Link to="/cardio" data-testid="cardio-cta" className="block border border-blue-500/40 hover:border-blue-500 p-3 mb-5 bg-blue-500/5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-500 flex items-center justify-center"><Heart className="w-5 h-5 text-white" /></div>
          <div className="flex-1">
            <div className="font-display text-lg uppercase">Cardio Session</div>
            <div className="text-xs text-neutral-400">Walk · Jog · Run · Bike · Row · more</div>
          </div>
          <ChevronRight className="w-5 h-5 text-blue-500" />
        </div>
      </Link>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-neutral-900 border border-neutral-800 rounded-none h-auto p-0">
          <TabsTrigger data-testid="tab-workouts" value="workouts" className="rounded-none h-10 px-5 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white">My Workouts</TabsTrigger>
          <TabsTrigger data-testid="tab-marketplace" value="marketplace" className="rounded-none h-10 px-3 sm:px-5 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white"><Flame className="w-3 h-3 mr-1 sm:mr-1.5 inline" />Marketplace</TabsTrigger>
          <TabsTrigger data-testid="tab-supersets" value="supersets" className="rounded-none h-10 px-5 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white">Supersets</TabsTrigger>
          <TabsTrigger data-testid="tab-exercises" value="exercises" className="rounded-none h-10 px-5 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white">Exercises</TabsTrigger>
        </TabsList>

        <TabsContent value="workouts" className="mt-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-neutral-400">{workouts.length} saved</div>
            <Button data-testid="new-workout-btn" onClick={() => nav("/workouts/new")} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9">
              <Plus className="w-4 h-4 mr-1" /> New
            </Button>
          </div>
          {workouts.length === 0 ? (
            <div className="border border-dashed border-neutral-800 p-8 text-center">
              <Dumbbell className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
              <div className="text-sm text-neutral-400">No workouts yet. Build your first one.</div>
            </div>
          ) : (
            <div className="divide-y divide-neutral-800 border border-neutral-800">
              {workouts.map((w) => (
                <div key={w.workout_id} data-testid={`workout-row-${w.workout_id}`} className="flex items-center gap-3 p-4 hover:bg-neutral-900">
                  <div className="flex-1 cursor-pointer" onClick={() => nav(`/workouts/${w.workout_id}`)}>
                    <div className="font-display text-xl uppercase">{w.name}</div>
                    <div className="text-xs text-neutral-500 uppercase tracking-wider">{w.category} · {w.exercises?.length || 0} exercises</div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => nav(`/workouts/${w.workout_id}`)} className="text-neutral-400 hover:text-blue-500"><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => nav(`/workouts/${w.workout_id}/history`)} data-testid={`history-workout-${w.workout_id}`} title="View previous results" className="text-neutral-400 hover:text-emerald-400"><History className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => openShareWorkout(w)} disabled={sharingWk === w.workout_id} data-testid={`share-workout-${w.workout_id}`} className="text-neutral-400 hover:text-blue-500">
                    {sharingWk === w.workout_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => deleteWorkout(w.workout_id)} data-testid={`delete-workout-${w.workout_id}`} className="text-neutral-400 hover:text-blue-500"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="marketplace" className="mt-5">
          <MarketplaceTab nav={nav} />
        </TabsContent>

        <TabsContent value="supersets" className="mt-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-neutral-400">{supersets.length} saved · drop-in to any workout</div>
            <Button data-testid="new-superset-btn" onClick={newSuperset} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9">
              <Plus className="w-4 h-4 mr-1" /> New
            </Button>
          </div>
          {supersets.length === 0 ? (
            <div className="border border-dashed border-neutral-800 p-8 text-center">
              <Layers className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
              <div className="text-sm text-neutral-400">No supersets yet. Combine 2+ exercises into one block and reuse them across workouts.</div>
            </div>
          ) : (
            <div className="divide-y divide-neutral-800 border border-neutral-800">
              {supersets.map((s) => (
                <div key={s.superset_id} data-testid={`ss-row-${s.superset_id}`} className="flex items-center gap-3 p-4 hover:bg-neutral-900">
                  <div className="w-10 h-10 border border-blue-500/40 flex items-center justify-center bg-blue-500/5">
                    <Layers className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="flex-1 cursor-pointer min-w-0" onClick={() => editSuperset(s)}>
                    <div className="font-display text-xl uppercase truncate">{s.name}</div>
                    <div className="text-xs text-neutral-500 uppercase tracking-wider truncate">
                      {s.exercises.length} exercises · {s.exercises.map((e) => e.name).join(" → ")}
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => editSuperset(s)} data-testid={`edit-ss-${s.superset_id}`} className="text-neutral-400 hover:text-blue-500"><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => openShare(s)} data-testid={`share-ss-${s.superset_id}`} disabled={sharing === s.superset_id} className="text-neutral-400 hover:text-blue-500">
                    {sharing === s.superset_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => deleteSuperset(s.superset_id)} data-testid={`delete-ss-${s.superset_id}`} className="text-neutral-400 hover:text-blue-500"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="exercises" className="mt-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-neutral-400">{exercises.length} exercises</div>
            <div className="flex gap-2">
              <Button data-testid="request-exercise-btn" onClick={() => setShowRequestEx(true)} variant="outline" className="bg-transparent border-neutral-700 text-white hover:border-blue-500 rounded-none uppercase tracking-wider font-display h-9 text-xs">
                <MessageSquarePlus className="w-3.5 h-3.5 mr-1" /> Request
              </Button>
              <Button data-testid="new-custom-exercise-btn" onClick={() => setShowCustomEx(true)} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9 text-xs">
                <Plus className="w-3.5 h-3.5 mr-1" /> Custom
              </Button>
            </div>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-neutral-500 absolute top-3 left-3" />
            <Input
              data-testid="exercise-search-input"
              placeholder="Search exercises…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 bg-neutral-900 border-neutral-800 focus:border-blue-500 rounded-none h-11 text-white"
            />
          </div>

          <div className="mt-4 flex gap-2 overflow-x-auto scrollbar-none pb-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`cat-chip-${c}`}
                onClick={() => setCategory(c)}
                className={`px-3 py-1.5 text-xs font-display tracking-wider uppercase border whitespace-nowrap ${
                  category === c ? "bg-blue-500 border-blue-500 text-white" : "bg-transparent border-neutral-700 text-neutral-400 hover:border-blue-500"
                }`}
              >{c}</button>
            ))}
          </div>

          <div className="mt-4 divide-y divide-neutral-800 border border-neutral-800">
            {filtered.map((e) => (
              <Link key={e.exercise_id} to={`/exercises/${e.exercise_id}`} data-testid={`exercise-row-${e.exercise_id}`} className="flex items-center gap-3 p-4 hover:bg-neutral-900">
                <div className="w-10 h-10 border border-neutral-800 flex items-center justify-center overflow-hidden bg-neutral-950">
                  {e.thumbnail_data_url ? (
                    <img src={e.thumbnail_data_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <Dumbbell className="w-5 h-5 text-blue-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2">
                    <span className="truncate">{e.name}</span>
                    {e.is_custom && <span className="px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-bold border border-blue-500/40 text-blue-500 shrink-0">Mine</span>}
                  </div>
                  <div className="text-xs text-neutral-500 uppercase tracking-wider">{e.category} · {e.muscle_groups.join(", ")}</div>
                </div>
                {e.is_custom && (
                  <>
                    <button
                      data-testid={`submit-custom-${e.exercise_id}`}
                      title="Submit to the public library"
                      onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); submitCustomToLibrary(e); }}
                      className="p-2 text-neutral-400 hover:text-emerald-400"
                    ><Send className="w-4 h-4" /></button>
                    <button
                      data-testid={`delete-custom-${e.exercise_id}`}
                      title="Delete custom exercise"
                      onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); deleteCustomExercise(e); }}
                      className="p-2 text-neutral-400 hover:text-red-400"
                    ><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
                <ChevronRight className="w-5 h-5 text-neutral-500" />
              </Link>
            ))}
            {filtered.length === 0 && <div className="p-8 text-center text-neutral-500 text-sm">No exercises match.</div>}
          </div>
        </TabsContent>
      </Tabs>
      <SupersetEditor open={showSSEditor} onOpenChange={setShowSSEditor} existing={editingSS} onSaved={handleSSSaved} />
      <CustomExerciseDialog open={showCustomEx} onOpenChange={setShowCustomEx} onCreated={reloadExercises} />
      <RequestExerciseDialog open={showRequestEx} onOpenChange={setShowRequestEx} />

      {/* Workout share dialog */}
      <Dialog open={!!shareWk} onOpenChange={(o) => !o && setShareWk(null)}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="wk-share-title" className="font-display text-2xl uppercase">Share Workout</DialogTitle>
            <DialogDescription className="text-xs text-neutral-400">
              Anyone with this link can copy <span className="text-blue-500 font-semibold">{shareWk?.workout?.name}</span> into their own library. Missing exercises are auto-added.
            </DialogDescription>
          </DialogHeader>
          {shareWk && (
            <div className="space-y-3">
              <div className="border border-neutral-800 bg-neutral-900 p-2 flex items-center gap-2">
                <div data-testid="wk-share-url" className="flex-1 text-xs text-neutral-300 truncate font-mono-d">{shareWk.url}</div>
                <Button data-testid="wk-share-copy-btn" size="icon" variant="ghost" onClick={copyShareWk} className="text-neutral-400 hover:text-blue-500 h-7 w-7"><Copy className="w-3.5 h-3.5" /></Button>
              </div>
              <Button data-testid="wk-share-native-btn" onClick={nativeShareWk} className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
              <div className="text-[10px] text-neutral-500 text-center uppercase tracking-widest">
                The link doesn't expire. Re-sharing returns the same link.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!shareSS} onOpenChange={(o) => !o && setShareSS(null)}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="ss-share-title" className="font-display text-2xl uppercase">Share to Squad</DialogTitle>
            <DialogDescription className="text-xs text-neutral-400">
              Anyone with this link can copy <span className="text-blue-500 font-semibold">{shareSS?.superset?.name}</span> into their own library.
            </DialogDescription>
          </DialogHeader>
          {shareSS && (
            <div className="space-y-3">
              <div className="border border-neutral-800 bg-neutral-900 p-2 flex items-center gap-2">
                <div data-testid="ss-share-url" className="flex-1 text-xs text-neutral-300 truncate font-mono-d">{shareSS.url}</div>
                <Button data-testid="ss-share-copy-btn" size="icon" variant="ghost" onClick={copyShare} className="text-neutral-400 hover:text-blue-500 h-7 w-7"><Copy className="w-3.5 h-3.5" /></Button>
              </div>
              <Button data-testid="ss-share-native-btn" onClick={nativeShare} className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
              <div className="text-[10px] text-neutral-500 text-center uppercase tracking-widest">
                The link doesn't expire. Re-sharing returns the same link.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MarketplaceTab({ nav }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importingToken, setImportingToken] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/marketplace/workouts");
        setItems(data || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const oneTapImport = async (it) => {
    setImportingToken(it.share_token);
    try {
      const { data: saved } = await api.post(`/workouts/by-token/${it.share_token}/import`);
      const added = saved.new_exercises_added || 0;
      toast.success(added > 0
        ? `"${saved.name}" added — ${added} new exercise${added === 1 ? "" : "s"} dropped in`
        : `"${saved.name}" added to your library`);
      // Refresh count silently — reload to reflect import
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not import");
    } finally {
      setImportingToken(null);
    }
  };

  if (loading) return (
    <div className="text-neutral-400 flex items-center gap-2 py-6">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading marketplace…
    </div>
  );
  if (items.length === 0) return (
    <div className="border border-dashed border-neutral-800 p-8 text-center" data-testid="marketplace-empty">
      <Flame className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
      <div className="text-sm text-neutral-400">No featured workouts yet — but as the squad starts sharing, the hottest ones land here.</div>
    </div>
  );

  return (
    <div data-testid="marketplace-grid" className="grid sm:grid-cols-2 gap-3">
      {items.map((it) => (
        <div
          key={it.workout_id}
          data-testid={`marketplace-wk-${it.workout_id}`}
          className="border border-neutral-800 bg-gradient-to-br from-blue-500/5 to-transparent p-4 flex flex-col"
        >
          <div className="flex items-center gap-2 mb-2">
            {it.is_featured && (
              <span className="px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-bold bg-blue-500 text-white inline-flex items-center gap-1">
                <Star className="w-2.5 h-2.5 fill-current" /> Coach Pick
              </span>
            )}
            {!it.is_featured && it.import_count > 0 && (
              <span className="px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-bold bg-neutral-800 text-blue-500 border border-blue-500/40">
                🔥 {it.import_count} imports
              </span>
            )}
            <span className="px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-bold border border-neutral-700 text-neutral-400 ml-auto">
              {it.category}
            </span>
          </div>
          <div className="font-display text-xl uppercase leading-tight mb-1 truncate">{it.name}</div>
          <div className="text-[10px] text-neutral-500 uppercase tracking-widest mb-2">By {it.owner_name}{it.owner_gym ? ` · ${it.owner_gym}` : ""}</div>
          {it.description && <div className="text-xs text-neutral-400 mb-2 line-clamp-2">{it.description}</div>}
          <div className="text-xs text-neutral-300 mb-3 space-y-0.5">
            {(it.exercises || []).slice(0, 3).map((e, idx) => (
              <div key={idx} className="flex items-center gap-1.5">
                <span className="font-display text-blue-500 w-4">{idx + 1}</span>
                <span className="truncate">{e.name}</span>
              </div>
            ))}
            {(it.exercises || []).length > 3 && (
              <div className="text-[10px] text-neutral-500 pl-5">+ {(it.exercises || []).length - 3} more</div>
            )}
          </div>
          <div className="mt-auto flex gap-2">
            <Button
              data-testid={`marketplace-add-${it.workout_id}`}
              onClick={() => oneTapImport(it)}
              disabled={importingToken === it.share_token}
              className="flex-1 bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9 text-xs"
            >
              {importingToken === it.share_token ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Adding…</>
              ) : (
                <><Download className="w-3 h-3 mr-1" /> One-tap add</>
              )}
            </Button>
            <button
              data-testid={`marketplace-preview-${it.workout_id}`}
              onClick={() => nav(`/workouts/import/${it.share_token}`)}
              className="px-3 h-9 inline-flex items-center justify-center border border-neutral-700 text-neutral-300 hover:text-white hover:border-blue-500 text-xs uppercase tracking-wider font-display"
            >
              Preview
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

